import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LowStockAlerts = ({ 
  lowStockProducts = [], 
  outOfStockProducts = [], 
  onAdjustStock 
}) => {
  const [adjustingProduct, setAdjustingProduct] = useState(null);
  const [adjustmentQuantity, setAdjustmentQuantity] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('manual_adjustment');
  const [adjustmentNotes, setAdjustmentNotes] = useState('');

  const reasons = [
    { value: 'manual_adjustment', label: 'Ajuste manual' },
    { value: 'supplier_delivery', label: 'Entrega de proveedor' },
    { value: 'inventory_count', label: 'Recuento de inventario' },
    { value: 'damaged_goods', label: 'Mercancía dañada' },
    { value: 'internal_use', label: 'Uso interno' }
  ];

  const handleStartAdjustment = (product) => {
    setAdjustingProduct(product);
    setAdjustmentQuantity('');
    setAdjustmentNotes('');
  };

  const handleSaveAdjustment = () => {
    const quantity = parseInt(adjustmentQuantity);
    
    if (adjustingProduct && !isNaN(quantity) && quantity !== 0) {
      onAdjustStock(
        adjustingProduct.id,
        quantity,
        adjustmentReason,
        adjustmentNotes || `Ajuste de stock: ${quantity > 0 ? 'incremento' : 'reducción'} de ${Math.abs(quantity)} unidades`
      );
      
      setAdjustingProduct(null);
      setAdjustmentQuantity('');
      setAdjustmentNotes('');
    }
  };

  const handleCancelAdjustment = () => {
    setAdjustingProduct(null);
    setAdjustmentQuantity('');
    setAdjustmentNotes('');
  };

  return (
    <div className="space-y-6">
      {/* Out of Stock Alert */}
      {outOfStockProducts.length > 0 && (
        <div className="bg-surface rounded-lg border border-error">
          <div className="p-4 bg-error-50 border-b border-error rounded-t-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-error-100 rounded-lg flex items-center justify-center">
                <Icon name="XCircle" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="font-semibold text-error">Sin Stock</h3>
                <p className="text-sm text-error-600">
                  {outOfStockProducts.length} producto{outOfStockProducts.length !== 1 ? 's' : ''} agotado{outOfStockProducts.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
          
          <div className="divide-y divide-border">
            {outOfStockProducts.map((product) => (
              <div key={product.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-text-primary">{product.name}</h4>
                    <p className="text-sm text-text-muted">{product.sku}</p>
                    <p className="text-xs text-text-muted">Mínimo: {product.minimumStock}</p>
                  </div>
                  <Button
                    variant="error"
                    size="sm"
                    onClick={() => handleStartAdjustment(product)}
                    iconName="Plus"
                  >
                    Reponer
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <div className="bg-surface rounded-lg border border-warning">
          <div className="p-4 bg-warning-50 border-b border-warning rounded-t-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-warning-100 rounded-lg flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <div>
                <h3 className="font-semibold text-warning">Stock Bajo</h3>
                <p className="text-sm text-warning-600">
                  {lowStockProducts.length} producto{lowStockProducts.length !== 1 ? 's' : ''} con stock bajo
                </p>
              </div>
            </div>
          </div>
          
          <div className="divide-y divide-border">
            {lowStockProducts.map((product) => (
              <div key={product.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-text-primary">{product.name}</h4>
                    <p className="text-sm text-text-muted">{product.sku}</p>
                    <div className="flex items-center space-x-4 text-xs text-text-muted mt-1">
                      <span>Stock actual: {product.currentStock}</span>
                      <span>Mínimo: {product.minimumStock}</span>
                    </div>
                  </div>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => handleStartAdjustment(product)}
                    iconName="Plus"
                  >
                    Ajustar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stock Adjustment Modal */}
      {adjustingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg border border-border w-full max-w-md">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-text-primary">
                  Ajustar Stock
                </h3>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCancelAdjustment}
                  iconName="X"
                />
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <h4 className="font-medium text-text-primary">{adjustingProduct.name}</h4>
                <p className="text-sm text-text-muted">{adjustingProduct.sku}</p>
                <p className="text-sm text-text-secondary">
                  Stock actual: {adjustingProduct.currentStock}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Cantidad de Ajuste
                </label>
                <Input
                  type="number"
                  value={adjustmentQuantity}
                  onChange={(e) => setAdjustmentQuantity(e.target.value)}
                  placeholder="Ej: +10 o -5"
                />
                <p className="mt-1 text-xs text-text-muted">
                  Use números positivos para aumentar stock, negativos para reducir
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Motivo
                </label>
                <select
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {reasons.map(reason => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Notas (opcional)
                </label>
                <textarea
                  value={adjustmentNotes}
                  onChange={(e) => setAdjustmentNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Detalles adicionales del ajuste..."
                />
              </div>

              {adjustmentQuantity && (
                <div className="bg-secondary-50 p-3 rounded-lg">
                  <p className="text-sm text-text-secondary">
                    Nuevo stock: {adjustingProduct.currentStock + parseInt(adjustmentQuantity || 0)}
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-border">
              <div className="flex space-x-3 justify-end">
                <Button
                  variant="secondary"
                  onClick={handleCancelAdjustment}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveAdjustment}
                  disabled={!adjustmentQuantity || adjustmentQuantity === '0'}
                >
                  Ajustar Stock
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Alerts */}
      {lowStockProducts.length === 0 && outOfStockProducts.length === 0 && (
        <div className="bg-surface rounded-lg border border-border p-8 text-center">
          <div className="w-12 h-12 bg-success-50 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={24} className="text-success" />
          </div>
          <h3 className="font-medium text-text-primary mb-2">Stock Óptimo</h3>
          <p className="text-text-muted">
            Todos los productos tienen stock suficiente
          </p>
        </div>
      )}
    </div>
  );
};

export default LowStockAlerts;